/**
 * useDashboardTabs Hook
 *
 * Manages dashboard tab navigation and configuration.
 * Handles tab state, tab change, and statistics card click navigation.
 * Includes loading state management for tab switching animations.
 */

import { useState, useCallback, useMemo, useRef, type RefObject } from 'react';
import { useSearchParams } from 'react-router';
import { parseURLFilters } from '../../types/search';
import { useMinimumLoadingDuration } from '../core/useMinimumLoadingDuration';

/**
 * Tab configuration for Polaris Tabs component
 */
export interface TabConfig {
  id: string;
  content: string;
  badge?: string;
  panelID: string;
  accessibilityLabel?: string;
}

/**
 * Tab badge counts and options
 */
export interface UseDashboardTabsOptions {
  activeAlertsCount: number;
  monitoredProductsCount: number;
}

/**
 * Return type for useDashboardTabs hook
 */
export interface UseDashboardTabsReturn {
  // State
  selectedTab: number;
  tabs: TabConfig[];
  /** True during brief loading state when switching tabs */
  isTabSwitching: boolean;
  /** The previous tab index (useful for transition animations) */
  previousTab: number;

  // Handlers
  handleTabChange: (selectedTabIndex: number) => void;
  handleStatisticsCardClick: (tabIndex: number, stockAlertsRef?: RefObject<HTMLDivElement | null>) => void;
}

/**
 * Duration (ms) for tab switch loading state.
 * Set to 0 for instant switching - there's no actual async data loading during tab switch.
 * The tab content is already loaded; this was previously 150ms which added unnecessary delay.
 * Keep the infrastructure in place for future async operations if needed.
 */
const TAB_SWITCH_DURATION = 0;

/**
 * Hook for managing dashboard tab navigation
 *
 * @param options - Badge counts for tabs
 * @returns Tab state and handlers
 */
export function useDashboardTabs(options: UseDashboardTabsOptions): UseDashboardTabsReturn {
  const [searchParams] = useSearchParams();

  // Parse initial tab from URL
  const initialFilters = parseURLFilters(searchParams);
  const [selectedTab, setSelectedTab] = useState(() => {
    const tabParam = initialFilters.tab;
    return tabParam ? parseInt(tabParam, 10) : 0;
  });

  // Track previous tab for transitions
  const previousTabRef = useRef(selectedTab);
  const [previousTab, setPreviousTab] = useState(selectedTab);

  // Tab switching loading state
  const [rawIsTabSwitching, setRawIsTabSwitching] = useState(false);
  const isTabSwitching = useMinimumLoadingDuration(rawIsTabSwitching, {
    minimumDuration: TAB_SWITCH_DURATION,
  });

  // Timeout ref for cleanup
  const switchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Destructure options
  const { activeAlertsCount, monitoredProductsCount } = options;

  // Build tabs configuration with dynamic badges
  const tabs = useMemo((): TabConfig[] => {
    return [
      {
        id: 'active-alerts',
        content: 'Active Alerts',
        badge: activeAlertsCount > 0 ? String(activeAlertsCount) : undefined,
        panelID: 'active-alerts-panel',
        accessibilityLabel: `Active Alerts (${activeAlertsCount})`,
      },
      {
        id: 'monitored-products',
        content: 'Monitored Products',
        badge: monitoredProductsCount > 0 ? String(monitoredProductsCount) : undefined,
        panelID: 'monitored-products-panel',
        accessibilityLabel: `Monitored Products (${monitoredProductsCount})`,
      },
      {
        id: 'alert-history',
        content: 'Alert History',
        panelID: 'alert-history-panel',
      },
    ];
  }, [activeAlertsCount, monitoredProductsCount]);

  /**
   * Handle tab change - simply switch tabs without changing scroll position
   *
   * NOTE: We intentionally don't modify scroll position on tab change because:
   * 1. The app runs in a Shopify Admin iframe where scrollIntoView/scrollTo
   *    don't work as expected (they scroll the iframe, not the parent page)
   * 2. The previous scroll restoration logic was scrolling to top (position 0)
   *    which was confusing to users
   * 3. Keeping scroll position stable provides the best UX - users stay where they are
   */
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    // Skip if same tab
    if (selectedTabIndex === selectedTab) return;

    // Clear any pending timeout
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
    }

    // Store previous tab before changing
    setPreviousTab(previousTabRef.current);

    // Start loading state
    setRawIsTabSwitching(true);

    // Update selected tab
    setSelectedTab(selectedTabIndex);
    previousTabRef.current = selectedTabIndex;

    // End loading state after tab switch duration
    switchTimeoutRef.current = setTimeout(() => {
      setRawIsTabSwitching(false);
    }, TAB_SWITCH_DURATION);
  }, [selectedTab]);

  /**
   * Handle statistics card click - switch to tab
   *
   * NOTE: scrollIntoView doesn't work reliably in Shopify Admin iframes
   * so we just switch the tab and let the user scroll manually if needed
   */
  const handleStatisticsCardClick = useCallback((tabIndex: number, _stockAlertsRef?: RefObject<HTMLDivElement | null>) => {
    // Only switch tab if not already selected
    if (selectedTab !== tabIndex) {
      setSelectedTab(tabIndex);
    }
  }, [selectedTab]);

  return {
    // State
    selectedTab,
    tabs,
    isTabSwitching,
    previousTab,

    // Handlers
    handleTabChange,
    handleStatisticsCardClick,
  };
}
